import { Problem } from '../generated/api/dsv-public/model';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, AccordionSummary, Button, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type ErrorFormProps = {
  problem?: Problem;
  errorResponseCode?: number;
  isSubmissionError?: boolean;
  canGoBack?: boolean;
  goBack?: () => void;
};

export const ErrorForm: React.FC<ErrorFormProps> = ({
  problem,
  errorResponseCode,
  isSubmissionError = false,
  canGoBack = false,
  goBack,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleToggleTechnicalDetails = useCallback(() => {
    setIsOpen(prev => !prev);
  }, [setIsOpen]);

  const genericErrorTitle = isSubmissionError ? t('forms.submission-failed.message') : t('forms.error-form.title');
  const statusCode = problem?.statusCode || errorResponseCode;
  const showGenericErrorTitle = problem?.problemCode == null || Number(statusCode) >= 500;
  const timestamp = problem?.timestamp || new Date().toISOString();
  const showGoBackButton = canGoBack && goBack != null;

  return (
    <>
      <h2>{showGenericErrorTitle ? genericErrorTitle : problem?.detail}</h2>
      <Accordion expanded={isOpen} onChange={handleToggleTechnicalDetails}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{t('forms.common.technical-details')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {Object.entries(problem?.technicalDetails || {}).map(([key, value]) => (
            <Typography key={key} textAlign="start">{`${key}: ${value}`}</Typography>
          ))}
          {statusCode && <Typography textAlign="start">StatusCode: {statusCode}</Typography>}
          {problem?.problemCode && <Typography textAlign="start">ProblemCode: {problem?.problemCode}</Typography>}
          {problem?.problemUuid && <Typography textAlign="start">ProblemUuid: {problem?.problemUuid}</Typography>}
          <Typography textAlign="start">Timestamp: {timestamp}</Typography>
        </AccordionDetails>
      </Accordion>
      {showGoBackButton && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={goBack}>
            {t('forms.common.back')}
          </Button>
        </Stack>
      )}
    </>
  );
};
